import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, AlertTriangle, CheckCircle2, XCircle, Clock,
  BookOpen, Target, TrendingUp, TrendingDown, ChevronRight,
  Loader2, FileText, Zap, Users, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import PowerScoreShareCard from '../components/PowerScoreShareCard';

// Risk level calculation
const getRiskLevel = (score) => {
  if (score >= 80) return { level: 'Low', color: 'green', icon: CheckCircle2 };
  if (score >= 60) return { level: 'Medium', color: 'yellow', icon: Clock };
  return { level: 'High', color: 'red', icon: AlertTriangle };
};

// Calculate Recruitment ROI
const calculateRecruitmentROI = (academic, tactical, brand) => {
  // Weighted calculation: Academic 40%, Tactical 30%, Brand 30%
  return Math.round((academic * 0.4) + (tactical * 0.3) + (brand * 0.3));
};

const ExecutiveReadinessReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState(null);
  const [parentScore, setParentScore] = useState(null);
  const [error, setError] = useState(null);

  const studentEmail = searchParams.get('student');

  useEffect(() => {
    if (studentEmail) {
      fetchStudentData();
    } else {
      // Try to get from localStorage
      const userData = localStorage.getItem('academy_user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.athlete_email) {
          fetchStudentDataByEmail(user.athlete_email);
        } else {
          setLoading(false);
          setError('No linked athlete found. Please complete the Parent Diagnostic first.');
        }
      } else {
        setLoading(false);
        setError('Please complete the Parent Diagnostic to view this report.');
      }
    }
  }, [studentEmail]);

  const fetchStudentData = async () => {
    try {
      const response = await api.get(`/academy/power-score/${studentEmail}`);
      if (response.data.success) {
        setStudentData({
          name: response.data.name,
          email: studentEmail,
          scores: response.data.current_score,
          growth: response.data.growth
        });
      } else {
        setError('Student has not completed their diagnostic yet.');
      }
    } catch (err) {
      console.error('Error fetching student data:', err);
      setError('Unable to load student data. They may not have completed their diagnostic.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDataByEmail = async (email) => {
    try {
      const response = await api.get(`/academy/power-score/${email}`);
      if (response.data.success) {
        setStudentData({
          name: response.data.name,
          email: email,
          scores: response.data.current_score,
          growth: response.data.growth
        });
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error || !studentData) {
    return (
      <div className="min-h-screen bg-black text-white pt-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <img 
            src="/images/scholar-parent-logo-new.png" 
            alt="Scholar Parent Academy" 
            className="h-32 mx-auto mb-8"
          />
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-4">{error || 'No Student Data Available'}</h1>
          <p className="text-gray-400 mb-8">
            To view the Executive Readiness Report, your athlete must first complete The Combine diagnostic.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/academy/parent-diagnostic')}
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
            >
              Complete Parent Diagnostic
            </Button>
            <Button
              onClick={() => navigate('/academy?type=parent')}
              variant="outline"
              className="border-purple-500/50 text-purple-400"
            >
              Return to Portal
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { scores } = studentData;
  const academic = scores.academic || 0;
  const tactical = scores.tactical || 0;
  const brand = scores.brand || 0;
  const totalScore = scores.total || Math.round((academic + tactical + brand) / 3);

  const academicRisk = getRiskLevel(academic);
  const brandRisk = getRiskLevel(brand);
  const recruitmentROI = calculateRecruitmentROI(academic, tactical, brand);

  const AcademicRiskIcon = academicRisk.icon;
  const BrandRiskIcon = brandRisk.icon;

  // Determine primary recommendation
  const getPrimaryRecommendation = () => {
    if (academic < 80) {
      return {
        title: 'Academic Recovery Pivot',
        subtitle: 'Powered by IXL',
        description: 'To protect the athlete\'s legacy and ensure academic eligibility remains intact through the Spring season, we recommend the Academic Recovery Pivot.',
        goal: 'Move from "At-Risk" to "Cleared" before the February circuit begins.'
      };
    } else if (brand < 80) {
      return {
        title: 'Legacy Branding Pivot',
        subtitle: 'Media Governance Track',
        description: 'To maximize NIL valuation and protect public image, we recommend the Legacy Branding Pivot to establish proper media governance.',
        goal: 'Achieve "Media Ready" status before recruitment intensifies.'
      };
    }
    return {
      title: 'Elite Maintenance Track',
      subtitle: 'Stay Ahead',
      description: 'Your athlete is performing at elite levels. The Elite Maintenance Track ensures they stay ahead of the competition.',
      goal: 'Maintain "Officially Cleared" status through continuous development.'
    };
  };

  const recommendation = getPrimaryRecommendation();

  return (
    <div className="min-h-screen bg-black text-white pt-20 pb-12">
      {/* Header */}
      <div className="bg-gradient-to-b from-purple-900/20 via-purple-900/10 to-black py-12 px-4 border-b border-purple-500/20">
        <div className="max-w-4xl mx-auto">
          {/* Scholar Parent Logo */}
          <div className="text-center mb-8">
            <img 
              src="/images/scholar-parent-logo-new.png" 
              alt="Scholar Parent Academy" 
              className="h-28 md:h-36 mx-auto mb-4"
            />
          </div>
          
          <div className="text-center">
            <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30 mb-4">
              EXECUTIVE READINESS REPORT
            </Badge>
            <h1 className="text-3xl md:text-4xl font-black mb-2">
              HOUSEHOLD EXECUTIVE SUMMARY:
            </h1>
            <p className="text-2xl text-purple-400 font-bold">{studentData.name}</p>
            <p className="text-gray-400 mt-2">Strategic Audit & Risk Assessment</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Executive Brief */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Executive Brief</h3>
                <p className="text-gray-300">
                  Your athlete has completed <span className="text-purple-400 font-bold">The Combine</span>. 
                  The data identifies specific <span className="text-white font-bold">Executive Gaps</span> that could impact 
                  long-term collegiate eligibility and NIL valuation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Assessment */}
        <h2 className="text-xl font-black text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-500" />
          RISK ASSESSMENT
        </h2>
        
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {/* Academic Liability */}
          <Card className={`border-2 ${
            academicRisk.color === 'green' ? 'bg-green-500/10 border-green-500/30' :
            academicRisk.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30' :
            'bg-red-500/10 border-red-500/30'
          }`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm uppercase tracking-wider">Academic Liability</p>
                <AcademicRiskIcon className={`w-5 h-5 ${
                  academicRisk.color === 'green' ? 'text-green-500' :
                  academicRisk.color === 'yellow' ? 'text-yellow-500' :
                  'text-red-500'
                }`} />
              </div>
              <p className={`text-3xl font-black ${
                academicRisk.color === 'green' ? 'text-green-500' :
                academicRisk.color === 'yellow' ? 'text-yellow-500' :
                'text-red-500'
              }`}>
                {academicRisk.level}
              </p>
              <p className="text-xs text-gray-500 mt-2">Based on IXL Diagnostic</p>
            </CardContent>
          </Card>

          {/* Brand Exposure */}
          <Card className={`border-2 ${
            brandRisk.color === 'green' ? 'bg-green-500/10 border-green-500/30' :
            brandRisk.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30' :
            'bg-red-500/10 border-red-500/30'
          }`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm uppercase tracking-wider">Brand Exposure</p>
                <BrandRiskIcon className={`w-5 h-5 ${
                  brandRisk.color === 'green' ? 'text-green-500' :
                  brandRisk.color === 'yellow' ? 'text-yellow-500' :
                  'text-red-500'
                }`} />
              </div>
              <p className={`text-3xl font-black ${
                brandRisk.color === 'green' ? 'text-green-500' :
                brandRisk.color === 'yellow' ? 'text-yellow-500' :
                'text-red-500'
              }`}>
                {brandRisk.level}
              </p>
              <p className="text-xs text-gray-500 mt-2">Based on Media Governance Audit</p>
            </CardContent>
          </Card>

          {/* Recruitment ROI */}
          <Card className="bg-purple-500/10 border-2 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm uppercase tracking-wider">Recruitment ROI</p>
                <DollarSign className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-3xl font-black text-purple-400">
                {recruitmentROI}<span className="text-lg">/100</span>
              </p>
              <p className="text-xs text-gray-500 mt-2">Composite Performance Index</p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Scores */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              Athlete Performance Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-black/30 rounded-lg">
                <BookOpen className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-white">{academic}%</p>
                <p className="text-xs text-gray-500">Academic</p>
              </div>
              <div className="text-center p-4 bg-black/30 rounded-lg">
                <Target className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-white">{tactical}%</p>
                <p className="text-xs text-gray-500">Tactical</p>
              </div>
              <div className="text-center p-4 bg-black/30 rounded-lg">
                <Shield className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-white">{brand}%</p>
                <p className="text-xs text-gray-500">Brand</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* The Governor's Decision */}
        <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/5 border-2 border-purple-500/30 mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-7 h-7 text-purple-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm text-purple-400 uppercase tracking-wider mb-1">The Governor's Decision</h3>
                <h4 className="text-xl font-black text-white mb-1">{recommendation.title}</h4>
                <p className="text-purple-400 text-sm mb-4">{recommendation.subtitle}</p>
                <p className="text-gray-300 mb-4">{recommendation.description}</p>
                
                <div className="bg-black/30 rounded-lg p-4 mb-6 border border-purple-500/20">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">The Goal</p>
                  <p className="text-white font-medium">{recommendation.goal}</p>
                </div>
                
                <Button
                  onClick={() => navigate('/academy/grandmaster')}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-black text-lg px-8 py-6 uppercase tracking-wider"
                  data-testid="activate-sprint-btn"
                >
                  Activate The 30-Day Sprint
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share Card */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-white mb-4 text-center">Share This Report</h3>
          <PowerScoreShareCard
            userName={studentData.name}
            totalScore={totalScore}
            academicScore={academic}
            tacticalScore={tactical}
            brandScore={brand}
            major="Executive Report"
            cohort="Winter '26"
            isParent={true}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/academy?type=parent')}
            variant="outline"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            Return to Parent Portal
          </Button>
          <Button
            onClick={() => navigate('/academy/parent-diagnostic')}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            Take Your Own Diagnostic
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveReadinessReport;
